import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Box,
  IconButton,
  Link,
  Grid,
  Paper,
  TextField,
  Card,
  CardContent,
  Grid as PersonalityGrid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getToneTraits, getPersonalities } from '../services/api';
import AdvisorIcon from '@mui/icons-material/Business';
import MotivatorIcon from '@mui/icons-material/EmojiEvents';
import EntertainerIcon from '@mui/icons-material/Celebration';
import EmpathicIcon from '@mui/icons-material/Favorite';
import TrendsetterIcon from '@mui/icons-material/TrendingUp';
import InnovatorIcon from '@mui/icons-material/Lightbulb';
import EducatorIcon from '@mui/icons-material/School';

const getPersonalityIcon = (type) => {
  const icons = {
    'Advisor': AdvisorIcon,
    'Motivator': MotivatorIcon,
    'Entertainer': EntertainerIcon,
    'Empathetic': EmpathicIcon,
    'Trendsetter': TrendsetterIcon,
    'Innovator': InnovatorIcon,
    'Educator': EducatorIcon,
  };
  const Icon = icons[type] || AdvisorIcon;
  return <Icon />;
};

const ToneOfVoiceDialog = ({ open, onClose, onSubmit, initialData = null }) => {
  const [availableTraits, setAvailableTraits] = useState([]);
  const [selectedTraits, setSelectedTraits] = useState([]);
  const [availablePersonalities, setAvailablePersonalities] = useState([]);
  const [personality, setPersonality] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    custom_instructions: '',
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    loadTraits();
    loadPersonalities();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        custom_instructions: initialData.custom_instructions || '',
      });
      setSelectedTraits(initialData.selected_traits || []);
      setPersonality(initialData.personality);
      setIsDefault(Boolean(initialData.is_default));
    } else {
      resetForm();
    }
  }, [initialData, open]);

  useEffect(() => {
    const isValid = formData.name.trim() !== '' && selectedTraits.length > 0;
    setIsFormValid(isValid);
  }, [formData, selectedTraits]);

  const loadTraits = async () => {
    try {
      const traits = await getToneTraits();
      setAvailableTraits(traits);
    } catch (error) {
      console.error('Error loading traits:', error);
    }
  };

  const loadPersonalities = async () => {
    try {
      const personalities = await getPersonalities();
      setAvailablePersonalities(personalities);
    } catch (error) {
      console.error('Error loading personalities:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      custom_instructions: '',
    });
    setSelectedTraits([]);
    setPersonality(null);
    setIsDefault(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    onSubmit({
      ...formData,
      personality_id: personality?.id || null,
      selected_traits: selectedTraits.map(trait => trait.id),
      user_id: 1,
      is_default: isDefault,
    });
    handleClose();
  };

  const handleTraitToggle = (trait) => {
    if (selectedTraits.find(t => t.id === trait.id)) {
      setSelectedTraits(selectedTraits.filter(t => t.id !== trait.id));
    } else if (selectedTraits.length < 5) {
      setSelectedTraits([...selectedTraits, trait]);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          maxHeight: '80vh',
          width: '1200px',
          maxWidth: '90vw',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6">
          {initialData ? 'Edit tone of voice' : 'Select traits'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={8}>
            {/* Basic Information */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                margin="normal"
                size="small"
                multiline
                rows={2}
              />
            </Box>

            {/* Traits Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Select up to 5 traits that describe your tone of voice
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                mb: 3 
              }}>
                {availableTraits.map((trait) => (
                  <Chip
                    key={trait.id}
                    label={trait.name}
                    onClick={() => handleTraitToggle(trait)}
                    variant={selectedTraits.find(t => t.id === trait.id) ? "filled" : "outlined"}
                    color={selectedTraits.find(t => t.id === trait.id) ? "primary" : "default"}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>
            </Box>

            {/* Personality Section */}
            <Box sx={{ mb: 3 }}>
              <Typography 
                component="div" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1 
                }}
              >
                <span>Add personality</span>
                <Typography 
                  component="span" 
                  color="text.secondary" 
                  sx={{ ml: 1 }}
                >
                  (optional)
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select a personality that matches this tone of voice
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={personality?.id || ''}
                  onChange={(e) => {
                    const selected = availablePersonalities.find(p => p.id === e.target.value);
                    setPersonality(selected || null);
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>No personality selected</em>
                  </MenuItem>
                  {availablePersonalities.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        width: '100%',
                        py: 1
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mb: 0.5
                        }}>
                          <Box sx={{ 
                            color: 'text.secondary',
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {getPersonalityIcon(p.name)}
                          </Box>
                          <Typography variant="subtitle1">
                            {p.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {p.description}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            fontStyle: 'italic',
                            mt: 0.5
                          }}
                        >
                          Examples: {p.examples || 'No examples available'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Custom Instructions */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Custom Instructions
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add any specific instructions or notes about this voice style"
                value={formData.custom_instructions}
                onChange={(e) => setFormData({ ...formData, custom_instructions: e.target.value })}
                size="small"
              />
            </Box>

            {/* Import Section */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              borderTop: 1,
              borderColor: 'divider',
              pt: 2
            }}>
              <Typography variant="body2" color="text.secondary">
                Already have a tone of voice defined?
              </Typography>
              <Link
                component="button"
                variant="body2"
                sx={{ ml: 1 }}
                onClick={() => {/* TODO: Implement import */}}
              >
                Import tone of voice
              </Link>
            </Box>

            {/* After Custom Instructions Box */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDefault}
                    onChange={(e) => {
                      setIsDefault(e.target.checked);
                    }}
                  />
                }
                label="Set as default tone of voice"
              />
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid item xs={4}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2,
                height: '100%',
                backgroundColor: 'grey.50'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Selected traits
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 1
              }}>
                {selectedTraits.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    No traits selected
                  </Typography>
                ) : (
                  selectedTraits.map((trait) => (
                    <Paper
                      key={trait.id}
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Typography variant="body2">
                        {trait.name}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => handleTraitToggle(trait)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid}
        >
          {initialData ? 'Save Changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToneOfVoiceDialog; 